

import { delay } from "./client";
import {
  CATEGORY_TABS,
  COLOR_FILTERS,
  HERO_SLIDES,
  NEW_ARRIVAL_SLUGS,
  PRODUCTS,
  SIZE_FILTERS,
} from "./mock-data";
import type { CategoryTab, Product } from "./types";

/** Minimal query-options helper mirroring the shape used by query factories. */
function queryOptions<T>(opts: { queryKey: unknown[]; queryFn: () => Promise<T>; enabled?: boolean }) {
  return opts;
}

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export type OrderStatus = "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";

export const ORDER_STATUSES: OrderStatus[] = [
  "pending",
  "confirmed",
  "shipped",
  "delivered",
  "cancelled",
];

/** Statuses that count towards sales figures. */
export const VALID_SALES_STATUSES: OrderStatus[] = ["pending", "confirmed", "shipped", "delivered"];

export interface AdminOrderItem {
  name: string;
  image: string;
  size: string;
  color: string;
  qty: number;
  price: number;
}

export interface AdminOrder {
  id: string;
  customer: string;
  email: string;
  phone: string;
  area: string;
  address: string;
  eta: string;
  createdAt: string;
  items: number;
  lineItems: AdminOrderItem[];
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  status: OrderStatus;
}

export type AdminRole = "customer" | "operator";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  orders: number;
  joinedAt: string;
}

export interface AdminProduct {
  id: string;
  slug: string;
  name: string;
  category: string;
  categorySlug: string;
  ref: string;
  price: number;
  stock: number;
  image: string;
  active: boolean;
}

export interface AdminColor {
  id: string;
  name: string;
  hex: string;
}

export interface AdminCategory {
  id: string;
  slug: string;
  label: string;
  parent?: string;
}

export interface AdminMetrics {
  revenue: number;
  orders: number;
  pending: number;
  listings: number;
  revenueTrend: number;
  ordersTrend: number;
}

export interface SalesPoint {
  month: string;
  revenue: number;
  orders: number;
}

export interface CategorySales {
  category: string;
  revenue: number;
}

export interface AppearanceSettings {
  slides: string[];
  intervalMs: number;
}

export interface GeneralSettings {
  currency: string;
  storeName: string;
  siteTitle: string;
  siteDescription: string;
  whatsapp: string;
  facebook: string;
  instagram: string;
  tiktok: string;
}

export interface ShippingArea {
  id: string;
  name: string;
  nameAr?: string;
  price: number;
  days: number;
  daysMin?: number;
  daysMax?: number;
  active: boolean;
}

export interface ShippingSettings {
  senderName: string;
  senderAccount: string;
  areas: ShippingArea[];
}

/* Analytics ---------------------------------------------------------------- */

export type RangePreset = "today" | "7d" | "30d" | "3m" | "1y" | "custom";

export interface AnalyticsRange {
  from: string;
  to: string;
}

export interface SalesTimePoint {
  date: string;
  sales: number;
  orders: number;
  aov: number;
}

export interface VisitorPoint {
  date: string;
  visitors: number;
  views: number;
}

export interface PathHit {
  path: string;
  hits: number;
}

export interface StatusSlice {
  status: OrderStatus;
  count: number;
}

export interface TopProduct {
  id: string;
  name: string;
  image: string;
  units: number;
  sales: number;
}

export interface AnalyticsSummary {
  range: AnalyticsRange;
  sales: number;
  orders: number;
  aov: number;
  visitors: number;
  views: number;
  series: SalesTimePoint[];
  traffic: VisitorPoint[];
  paths: PathHit[];
  statuses: StatusSlice[];
  topProducts: TopProduct[];
}

/* -------------------------------------------------------------------------- */
/*  In-memory mock state (session-scoped, no backend)                         */
/* -------------------------------------------------------------------------- */

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const uid = () => Math.random().toString(36).slice(2, 9);
const iso = (date: Date) => date.toISOString().slice(0, 10);
const daysAgo = (days: number) => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date;
};

const CUSTOMERS = [
  ["Layla Hassan", "layla@example.com", "0101 234 5566", "New Cairo"],
  ["Nour Farid", "nour@example.com", "0112 887 4410", "Zamalek"],
  ["Amira Saleh", "amira@example.com", "0100 665 2231", "Sheikh Zayed"],
  ["Hana Youssef", "hana@example.com", "0128 340 9912", "Maadi"],
  ["Salma Adel", "salma@example.com", "0106 771 5540", "Heliopolis"],
  ["Dina Kamal", "dina@example.com", "0114 220 8873", "Alexandria"],
  ["Mariam Zaki", "mariam@example.com", "0100 908 3312", "6th of October"],
  ["Farah Nabil", "farah@example.com", "0122 554 6690", "Nasr City"],
];

const SHIPPING_FEE = 25;

function buildLineItems(index: number): AdminOrderItem[] {
  const count = (index % 3) + 1;
  return Array.from({ length: count }, (_, i) => {
    const product = PRODUCTS[(index * 3 + i) % PRODUCTS.length]!;
    return {
      name: product.name,
      image: product.images[0]!,
      size: product.sizes[i % product.sizes.length] ?? "M",
      color: product.colors[i % product.colors.length]?.name ?? "Natural",
      qty: ((index + i) % 2) + 1,
      price: product.price,
    };
  });
}

function priceOrder(order: Omit<AdminOrder, "subtotal" | "total" | "items">): AdminOrder {
  const subtotal = order.lineItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  return {
    ...order,
    items: order.lineItems.reduce((sum, item) => sum + item.qty, 0),
    subtotal,
    total: Math.max(0, subtotal + order.shipping - order.discount),
  };
}

let orders: AdminOrder[] = Array.from({ length: 26 }, (_, i) => {
  const [customer, email, phone, area] = CUSTOMERS[i % CUSTOMERS.length]!;
  return priceOrder({
    id: `SEEN-${100420 + i * 7}`,
    customer: customer!,
    email: email!,
    phone: phone!,
    area: area!,
    address: `${12 + i} Nile View, ${area}`,
    eta: `${2 + (i % 3)}-${4 + (i % 3)} days`,
    createdAt: iso(daysAgo(i % 40)),
    lineItems: buildLineItems(i),
    shipping: SHIPPING_FEE,
    discount: 0,
    status: ORDER_STATUSES[i % ORDER_STATUSES.length]!,
  });
});

let users: AdminUser[] = [
  ...CUSTOMERS.map(([name, email], i) => ({
    id: `u${i + 1}`,
    name: name!,
    email: email!,
    role: "customer" as AdminRole,
    orders: (i * 3) % 9,
    joinedAt: `2026-0${(i % 6) + 1}-1${i % 9}`,
  })),
  {
    id: "op1",
    name: "Studio Operator",
    email: "studio@seen.com",
    role: "operator",
    orders: 0,
    joinedAt: "2026-01-04",
  },
  {
    id: "op2",
    name: "Atelier Manager",
    email: "atelier@seen.com",
    role: "operator",
    orders: 0,
    joinedAt: "2026-02-11",
  },
];

const toAdminProduct = (product: Product, index: number): AdminProduct => ({
  id: product.id,
  slug: product.slug,
  name: product.name,
  category: product.category,
  categorySlug: product.categorySlug,
  ref: product.ref,
  price: product.price,
  stock: 6 + ((index * 11) % 40),
  image: product.images[0]!,
  active: true,
});

let products: AdminProduct[] = PRODUCTS.map(toAdminProduct);

let colors: AdminColor[] = COLOR_FILTERS.map((color) => ({ id: uid(), ...color }));

let categories: AdminCategory[] = CATEGORY_TABS.map((tab) => ({ id: uid(), ...tab }));

let subcategories: AdminCategory[] = (() => {
  const seen = new Map<string, AdminCategory>();
  for (const product of PRODUCTS) {
    const slug = product.category.toLowerCase().replace(/\s+/g, "-");
    if (!seen.has(slug)) {
      seen.set(slug, {
        id: uid(),
        slug,
        label: product.category,
        parent: product.categorySlug,
      });
    }
  }
  return Array.from(seen.values());
})();

let newArrivals: string[] = [...NEW_ARRIVAL_SLUGS];

let appearance: AppearanceSettings = {
  slides: [...HERO_SLIDES],
  intervalMs: 4000,
};

let general: GeneralSettings = {
  currency: "$",
  storeName: "SEEN",
  siteTitle: "SEEN | Quiet Luxury Womenswear",
  siteDescription:
    "SEEN is a womenswear house of quiet luxury — silk dresses, tailored coats and artisanal accessories.",
  whatsapp: "",
  facebook: "https://www.facebook.com/seen",
  instagram: "https://www.instagram.com/seen",
  tiktok: "https://www.tiktok.com/@seen",
};

let shipping: ShippingSettings = {
  senderName: "SEEN Atelier",
  senderAccount: "0123456789",
  areas: [
    { id: uid(), name: "Greater Cairo & Giza", nameAr: "كل المناطق داخل القاهرة والجيزة", price: 65, days: 3, active: true },
    { id: uid(), name: "Alexandria", nameAr: "كل مناطق الإسكندرية", price: 75, days: 4, active: true },
    { id: uid(), name: "Ismailia, Suez, Port Said", nameAr: "الإسماعيلية، السويس، بورسعيد", price: 75, days: 3, active: true },
    { id: uid(), name: "Delta & Canal cities", nameAr: "طنطا، الغربية، المنصورة، دمياط", price: 75, days: 4, active: true },
    { id: uid(), name: "Fayoum, Beni Suef", nameAr: "الفيوم، بني سويف", price: 85, days: 5, active: true },
    { id: uid(), name: "Sohag, Minya, Assiut", nameAr: "سوهاج، المنيا، أسيوط", price: 85, days: 5, active: true },
    { id: uid(), name: "Qena, Luxor, Aswan", nameAr: "قنا، الأقصر، أسوان", price: 90, days: 5, active: true },
    { id: uid(), name: "Red Sea & North Coast", nameAr: "البحر الأحمر، الساحل الشمالي", price: 135, days: 5, active: false },
  ],
};

/* -------------------------------------------------------------------------- */
/*  Visit tracking (mirrors a future POST /track + page_visits table)         */
/* -------------------------------------------------------------------------- */

interface PageVisit {
  id: string;
  path: string;
  sessionId: string;
  visitorId: string;
  at: string;
}

/** Stable, non-reversible visitor hash — never stores raw IP / user agent. */
function deriveVisitorId(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return `v${Math.abs(hash).toString(36)}`;
}

const SESSION_WINDOW_MS = 30 * 60 * 1000;
const TRACKED_PATHS = ["/", "/shop", "/product/silk-column-dress", "/cart", "/favorites", "/returns"];

let visits: PageVisit[] = (() => {
  const seeded: PageVisit[] = [];
  for (let day = 0; day < 90; day += 1) {
    const date = daysAgo(day);
    const views = 40 + ((day * 17) % 90);
    for (let i = 0; i < views; i += 1) {
      const visitorId = deriveVisitorId(`seed-${day % 7}-${i % (12 + (day % 9))}`);
      seeded.push({
        id: uid(),
        path: TRACKED_PATHS[(day + i) % TRACKED_PATHS.length]!,
        sessionId: `${visitorId}-${day}-${i}`,
        visitorId,
        at: date.toISOString(),
      });
    }
  }
  return seeded;
})();

const seenSessions = new Set<string>();

/** Records a storefront page view with double de-duplication. */
export function trackVisit(path: string, sessionId?: string): void {
  const visitorId = deriveVisitorId(
    typeof navigator === "undefined" ? "server" : `${navigator.userAgent}-${navigator.language}`,
  );
  const session = sessionId ?? `${visitorId}-${path}`;
  if (seenSessions.has(session)) return;
  seenSessions.add(session);

  const now = Date.now();
  const recent = visits.some(
    (visit) =>
      visit.visitorId === visitorId &&
      visit.path === path &&
      now - new Date(visit.at).getTime() < SESSION_WINDOW_MS,
  );
  if (recent) return;

  visits = [...visits, { id: uid(), path, sessionId: session, visitorId, at: new Date().toISOString() }];
}

export async function clearVisits(): Promise<void> {
  visits = [];
  seenSessions.clear();
  return delay(undefined, 220);
}

/* -------------------------------------------------------------------------- */
/*  Read helpers                                                              */
/* -------------------------------------------------------------------------- */

export function presetRange(preset: Exclude<RangePreset, "custom">): AnalyticsRange {
  const days = preset === "today" ? 0 : preset === "7d" ? 6 : preset === "30d" ? 29 : preset === "3m" ? 89 : 364;
  return { from: iso(daysAgo(days)), to: iso(daysAgo(0)) };
}

export async function getMetrics(): Promise<AdminMetrics> {
  const valid = orders.filter((order) => VALID_SALES_STATUSES.includes(order.status));
  const revenue = valid.reduce((sum, order) => sum + order.total, 0);
  return delay(
    {
      revenue,
      orders: orders.length,
      pending: orders.filter((order) => order.status === "pending").length,
      listings: products.filter((product) => product.active).length,
      revenueTrend: 12.4,
      ordersTrend: 8.1,
    },
    200,
  );
}

export async function getSalesSeries(): Promise<SalesPoint[]> {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"];
  return delay(
    months.map((month, i) => ({
      month,
      revenue: 8400 + ((i * 2137) % 9600),
      orders: 24 + ((i * 13) % 38),
    })),
    200,
  );
}

export async function getCategorySales(): Promise<CategorySales[]> {
  return delay(
    categories.map((category, i) => ({
      category: category.label,
      revenue: 4200 + ((i * 3311) % 11000),
    })),
    200,
  );
}

export async function getAnalytics(range: AnalyticsRange): Promise<AnalyticsSummary> {
  const from = new Date(`${range.from}T00:00:00`).getTime();
  const to = new Date(`${range.to}T23:59:59`).getTime();

  const inRange = orders.filter((order) => {
    const at = new Date(`${order.createdAt}T12:00:00`).getTime();
    return at >= from && at <= to;
  });
  const valid = inRange.filter((order) => VALID_SALES_STATUSES.includes(order.status));
  const rangeVisits = visits.filter((visit) => {
    const at = new Date(visit.at).getTime();
    return at >= from && at <= to;
  });

  const days = Math.max(1, Math.round((to - from) / 86400000) + 1);
  const buckets: string[] = Array.from({ length: Math.min(days, 90) }, (_, i) =>
    iso(new Date(from + (days - 1 - i) * 86400000)),
  ).reverse();

  const series: SalesTimePoint[] = buckets.map((date) => {
    const dayOrders = valid.filter((order) => order.createdAt === date);
    const sales = dayOrders.reduce((sum, order) => sum + order.total, 0);
    return {
      date,
      sales,
      orders: dayOrders.length,
      aov: dayOrders.length ? Math.round(sales / dayOrders.length) : 0,
    };
  });

  const traffic: VisitorPoint[] = buckets.map((date) => {
    const dayVisits = rangeVisits.filter((visit) => visit.at.slice(0, 10) === date);
    return {
      date,
      views: dayVisits.length,
      visitors: new Set(dayVisits.map((visit) => visit.visitorId)).size,
    };
  });

  const pathMap = new Map<string, number>();
  for (const visit of rangeVisits) pathMap.set(visit.path, (pathMap.get(visit.path) ?? 0) + 1);
  const paths = Array.from(pathMap, ([path, hits]) => ({ path, hits })).sort(
    (a, b) => b.hits - a.hits,
  );

  const statuses: StatusSlice[] = ORDER_STATUSES.map((status) => ({
    status,
    count: inRange.filter((order) => order.status === status).length,
  })).filter((slice) => slice.count > 0);

  const productMap = new Map<string, TopProduct>();
  for (const order of valid) {
    for (const item of order.lineItems) {
      const existing = productMap.get(item.name);
      const entry: TopProduct = existing ?? {
        id: item.name,
        name: item.name,
        image: item.image,
        units: 0,
        sales: 0,
      };
      entry.units += item.qty;
      entry.sales += item.qty * item.price;
      productMap.set(item.name, entry);
    }
  }
  const topProducts = Array.from(productMap.values())
    .sort((a, b) => b.sales - a.sales)
    .slice(0, 6);

  const sales = valid.reduce((sum, order) => sum + order.total, 0);

  return delay(
    {
      range,
      sales,
      orders: inRange.length,
      aov: valid.length ? Math.round(sales / valid.length) : 0,
      visitors: new Set(rangeVisits.map((visit) => visit.visitorId)).size,
      views: rangeVisits.length,
      series,
      traffic,
      paths,
      statuses,
      topProducts,
    },
    240,
  );
}

export async function getRawAnalytics(): Promise<any> {
  const items = orders.flatMap(o => o.lineItems.map(i => ({ ...i, order_id: o.id, product_id: i.name, created_at: o.createdAt, unit_price: i.price, quantity: i.qty })));
  const mappedOrders = orders.map(o => ({ ...o, created_at: o.createdAt }));
  const mappedProducts = products.map(p => ({ ...p, main_image_url: p.image }));
  const mappedVisits = visits.map(v => ({ ...v, created_at: v.at, visitor_id: v.visitorId, session_id: v.sessionId }));
  return delay({ orders: mappedOrders, items, products: mappedProducts, visits: mappedVisits }, 200);
}

export async function getOrders(): Promise<AdminOrder[]> {
  return delay(clone(orders), 220);
}

export async function updateOrderStatus(id: string, status: OrderStatus): Promise<AdminOrder> {
  orders = orders.map((order) => (order.id === id ? { ...order, status } : order));
  const updated = orders.find((order) => order.id === id);
  if (!updated) throw new Error("Order not found.");
  return delay(clone(updated), 180);
}

export async function updateOrder(input: {
  id: string;
  status: OrderStatus;
  discount: number;
}): Promise<AdminOrder> {
  if (input.discount < 0) throw new Error("Discount cannot be negative.");
  orders = orders.map((order) => {
    if (order.id !== input.id) return order;
    if (input.discount > order.subtotal + order.shipping) {
      throw new Error("Discount cannot exceed the order total.");
    }
    return priceOrder({ ...order, status: input.status, discount: input.discount });
  });
  const updated = orders.find((order) => order.id === input.id);
  if (!updated) throw new Error("Order not found.");
  return delay(clone(updated), 200);
}

export async function getUsers(): Promise<AdminUser[]> {
  return delay(clone(users), 200);
}

export async function updateUserRole(id: string, role: AdminRole): Promise<AdminUser> {
  users = users.map((user) => (user.id === id ? { ...user, role } : user));
  const updated = users.find((user) => user.id === id);
  if (!updated) throw new Error("User not found.");
  return delay(clone(updated), 160);
}

export async function getAdminProducts(): Promise<AdminProduct[]> {
  return delay(clone(products), 220);
}

export async function saveAdminProduct(input: AdminProduct): Promise<AdminProduct> {
  if (!input.name.trim()) throw new Error("Product name is required.");
  const exists = products.some((product) => product.id === input.id);
  products = exists
    ? products.map((product) => (product.id === input.id ? { ...input } : product))
    : [{ ...input, id: input.id || uid() }, ...products];
  return delay(clone(input), 180);
}

export async function deleteAdminProduct(id: string): Promise<void> {
  products = products.filter((product) => product.id !== id);
  return delay(undefined, 150);
}

export async function getColors(): Promise<AdminColor[]> {
  return delay(clone(colors), 160);
}

export async function saveColor(input: AdminColor): Promise<AdminColor> {
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(input.hex)) throw new Error("Enter a valid hex value.");
  const exists = colors.some((color) => color.id === input.id);
  colors = exists
    ? colors.map((color) => (color.id === input.id ? { ...input } : color))
    : [...colors, { ...input, id: input.id || uid() }];
  return delay(clone(input), 150);
}

export async function deleteColor(id: string): Promise<void> {
  colors = colors.filter((color) => color.id !== id);
  return delay(undefined, 140);
}

/* -------------------------------------------------------------------------- */
/*  Product colour variants (per product, with image galleries)               */
/* -------------------------------------------------------------------------- */

export interface ProductColorImage {
  id: string;
  colorId: string;
  url: string;
  sortOrder: number;
}

export interface ProductColorVariant {
  id: string;
  productId: string;
  nameEn: string;
  nameAr: string;
  hex: string;
  sortOrder: number;
  images: ProductColorImage[];
}

/** Seeded variants: two colours per product, images taken from the catalogue. */
let productColors: ProductColorVariant[] = PRODUCTS.flatMap((product, index) =>
  COLOR_FILTERS.slice(index % 2, (index % 2) + 2).map((color, position) => {
    const colorId = uid();
    return {
      id: colorId,
      productId: product.id,
      nameEn: color.name,
      nameAr: color.name,
      hex: color.hex,
      sortOrder: position,
      images: product.images.slice(0, 2).map((url, imageIndex) => ({
        id: uid(),
        colorId,
        url,
        sortOrder: imageIndex,
      })),
    };
  }),
);

const sortVariants = (list: ProductColorVariant[]) =>
  [...list]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((variant) => ({
      ...variant,
      images: [...variant.images].sort((a, b) => a.sortOrder - b.sortOrder),
    }));

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export async function getProductColors(productId: string): Promise<ProductColorVariant[]> {
  return delay(
    clone(sortVariants(productColors.filter((variant) => variant.productId === productId))),
    180,
  );
}

/** Sizes available for a product (mock: shared size scale). */
export function productSizes(): string[] {
  return [...SIZE_FILTERS];
}

export async function createProductColor(input: {
  productId: string;
  nameEn: string;
  nameAr: string;
  hex: string;
  saveAsPreset?: boolean;
}): Promise<ProductColorVariant> {
  if (!input.nameEn.trim()) throw new Error("Colour name (EN) is required.");
  if (!HEX_RE.test(input.hex)) throw new Error("Enter a valid hex value.");

  const siblings = productColors.filter((variant) => variant.productId === input.productId);
  const variant: ProductColorVariant = {
    id: uid(),
    productId: input.productId,
    nameEn: input.nameEn.trim(),
    nameAr: input.nameAr.trim() || input.nameEn.trim(),
    hex: input.hex,
    sortOrder: siblings.length,
    images: [],
  };
  productColors = [...productColors, variant];

  if (input.saveAsPreset && !colors.some((color) => color.hex.toLowerCase() === input.hex.toLowerCase())) {
    colors = [...colors, { id: uid(), name: variant.nameEn, hex: variant.hex }];
  }

  return delay(clone(variant), 180);
}

export async function updateProductColor(input: {
  id: string;
  nameEn: string;
  nameAr: string;
  hex: string;
}): Promise<ProductColorVariant> {
  if (!input.nameEn.trim()) throw new Error("Colour name (EN) is required.");
  if (!HEX_RE.test(input.hex)) throw new Error("Enter a valid hex value.");
  productColors = productColors.map((variant) =>
    variant.id === input.id
      ? { ...variant, nameEn: input.nameEn.trim(), nameAr: input.nameAr.trim(), hex: input.hex }
      : variant,
  );
  const updated = productColors.find((variant) => variant.id === input.id);
  if (!updated) throw new Error("Colour not found.");
  return delay(clone(updated), 160);
}

export async function deleteProductColor(id: string): Promise<void> {
  productColors = productColors.filter((variant) => variant.id !== id);
  return delay(undefined, 150);
}

/** Swaps sort_order with the neighbour in the given direction. */
export async function reorderProductColor(input: {
  id: string;
  direction: "up" | "down";
}): Promise<void> {
  const current = productColors.find((variant) => variant.id === input.id);
  if (!current) throw new Error("Colour not found.");
  const siblings = sortVariants(
    productColors.filter((variant) => variant.productId === current.productId),
  );
  const index = siblings.findIndex((variant) => variant.id === input.id);
  const target = siblings[input.direction === "up" ? index - 1 : index + 1];
  if (!target) return delay(undefined, 60);
  const currentOrder = current.sortOrder;
  productColors = productColors.map((variant) => {
    if (variant.id === current.id) return { ...variant, sortOrder: target.sortOrder };
    if (variant.id === target.id) return { ...variant, sortOrder: currentOrder };
    return variant;
  });
  return delay(undefined, 140);
}

export async function addProductColorImage(input: {
  colorId: string;
  url: string;
}): Promise<ProductColorImage> {
  const variant = productColors.find((item) => item.id === input.colorId);
  if (!variant) throw new Error("Colour not found.");
  const image: ProductColorImage = {
    id: uid(),
    colorId: input.colorId,
    url: input.url,
    sortOrder: variant.images.length,
  };
  productColors = productColors.map((item) =>
    item.id === input.colorId ? { ...item, images: [...item.images, image] } : item,
  );
  return delay(clone(image), 200);
}

export async function deleteProductColorImage(input: {
  colorId: string;
  imageId: string;
}): Promise<void> {
  productColors = productColors.map((item) =>
    item.id === input.colorId
      ? { ...item, images: item.images.filter((image) => image.id !== input.imageId) }
      : item,
  );
  return delay(undefined, 140);
}

export async function reorderProductColorImage(input: {
  colorId: string;
  imageId: string;
  direction: "up" | "down";
}): Promise<void> {
  const variant = productColors.find((item) => item.id === input.colorId);
  if (!variant) throw new Error("Colour not found.");
  const images = [...variant.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const index = images.findIndex((image) => image.id === input.imageId);
  const target = images[input.direction === "up" ? index - 1 : index + 1];
  const current = images[index];
  if (!target || !current) return delay(undefined, 60);
  const currentOrder = current.sortOrder;
  productColors = productColors.map((item) =>
    item.id === input.colorId
      ? {
          ...item,
          images: item.images.map((image) => {
            if (image.id === current.id) return { ...image, sortOrder: target.sortOrder };
            if (image.id === target.id) return { ...image, sortOrder: currentOrder };
            return image;
          }),
        }
      : item,
  );
  return delay(undefined, 140);
}

/** Mock blob upload — returns a local object URL standing in for storage. */
export async function uploadBlob(file: File): Promise<string> {
  await delay(undefined, 600);
  return URL.createObjectURL(file);
}


export async function getCategories(): Promise<AdminCategory[]> {
  return delay(clone(categories), 160);
}

export async function saveCategory(input: AdminCategory): Promise<AdminCategory> {
  if (!input.label.trim() || !input.slug.trim()) throw new Error("Label and slug are required.");
  const exists = categories.some((category) => category.id === input.id);
  categories = exists
    ? categories.map((category) => (category.id === input.id ? { ...input } : category))
    : [...categories, { ...input, id: input.id || uid() }];
  return delay(clone(input), 150);
}

export async function deleteCategory(id: string): Promise<void> {
  categories = categories.filter((category) => category.id !== id);
  return delay(undefined, 140);
}

export async function getSubcategoryRows(): Promise<AdminCategory[]> {
  return delay(clone(subcategories), 160);
}

export async function saveSubcategory(input: AdminCategory): Promise<AdminCategory> {
  if (!input.label.trim() || !input.slug.trim()) throw new Error("Label and slug are required.");
  const exists = subcategories.some((row) => row.id === input.id);
  subcategories = exists
    ? subcategories.map((row) => (row.id === input.id ? { ...input } : row))
    : [...subcategories, { ...input, id: input.id || uid() }];
  return delay(clone(input), 150);
}

export async function deleteSubcategory(id: string): Promise<void> {
  subcategories = subcategories.filter((row) => row.id !== id);
  return delay(undefined, 140);
}

export async function getNewArrivalRows(): Promise<AdminProduct[]> {
  return delay(
    clone(products.filter((product) => newArrivals.includes(product.slug))),
    180,
  );
}

export async function toggleNewArrival(slug: string): Promise<string[]> {
  newArrivals = newArrivals.includes(slug)
    ? newArrivals.filter((item) => item !== slug)
    : [...newArrivals, slug];
  return delay([...newArrivals], 150);
}

export async function getNewArrivalSlugs(): Promise<string[]> {
  return delay([...newArrivals], 120);
}

export async function getAppearance(): Promise<AppearanceSettings> {
  return delay(clone(appearance), 160);
}

export async function saveAppearance(input: AppearanceSettings): Promise<AppearanceSettings> {
  if (input.intervalMs < 1000) throw new Error("Interval must be at least 1000 ms.");
  appearance = clone(input);
  return delay(clone(appearance), 180);
}

export async function getGeneralSettings(): Promise<GeneralSettings> {
  return delay(clone(general), 160);
}

export async function saveGeneralSettings(input: GeneralSettings): Promise<GeneralSettings> {
  if (!input.siteTitle.trim()) throw new Error("Site title is required.");
  general = clone(input);
  return delay(clone(general), 180);
}

export async function getShippingSettings(): Promise<ShippingSettings> {
  return delay(clone(shipping), 160);
}

export async function saveShippingSettings(input: ShippingSettings): Promise<ShippingSettings> {
  if (input.areas.some((area) => !area.name.trim())) throw new Error("Every area needs a name.");
  if (input.areas.some((area) => area.price < 0 || area.days < 1)) {
    throw new Error("Price and delivery days must be positive.");
  }
  shipping = clone(input);
  return delay(clone(shipping), 200);
}

/** Active shipping zones consumed by checkout and the shipping profile. */
export async function getShippingAreas(): Promise<ShippingArea[]> {
  return delay(clone(shipping.areas.filter((area) => area.active)), 140);
}



/** Catalogue categories exposed for select inputs. */
export function categoryOptions(): CategoryTab[] {
  return categories.map(({ slug, label }) => ({ slug, label }));
}

/* -------------------------------------------------------------------------- */
/*  Query options                                                             */
/* -------------------------------------------------------------------------- */

export const adminMetricsQuery = () =>
  queryOptions({ queryKey: ["admin", "metrics"], queryFn: getMetrics });
export const adminSalesQuery = () =>
  queryOptions({ queryKey: ["admin", "sales"], queryFn: getSalesSeries });
export const adminCategorySalesQuery = () =>
  queryOptions({ queryKey: ["admin", "category-sales"], queryFn: getCategorySales });
export const adminAnalyticsQuery = (range: AnalyticsRange) =>
  queryOptions({
    queryKey: ["admin", "analytics", range.from, range.to],
    queryFn: () => getAnalytics(range),
  });
export const adminOrdersQuery = () =>
  queryOptions({ queryKey: ["admin", "orders"], queryFn: getOrders });
export const adminUsersQuery = () =>
  queryOptions({ queryKey: ["admin", "users"], queryFn: getUsers });
export const adminProductsQuery = () =>
  queryOptions({ queryKey: ["admin", "products"], queryFn: getAdminProducts });
export const adminColorsQuery = () =>
  queryOptions({ queryKey: ["admin", "colors"], queryFn: getColors });
export const productColorsQuery = (productId: string) =>
  queryOptions({
    queryKey: ["admin", "product-colors", productId],
    queryFn: () => getProductColors(productId),
    enabled: Boolean(productId),
  });
export const adminCategoriesQuery = () =>
  queryOptions({ queryKey: ["admin", "categories"], queryFn: getCategories });
export const adminSubcategoriesQuery = () =>
  queryOptions({ queryKey: ["admin", "subcategories"], queryFn: getSubcategoryRows });
export const adminNewArrivalsQuery = () =>
  queryOptions({ queryKey: ["admin", "new-arrivals"], queryFn: getNewArrivalSlugs });
export const adminAppearanceQuery = () =>
  queryOptions({ queryKey: ["admin", "appearance"], queryFn: getAppearance });
export const adminGeneralQuery = () =>
  queryOptions({ queryKey: ["admin", "general"], queryFn: getGeneralSettings });
export const adminShippingQuery = () =>
  queryOptions({ queryKey: ["admin", "shipping"], queryFn: getShippingSettings });
export const shippingAreasQuery = () =>
  queryOptions({ queryKey: ["shipping", "areas"], queryFn: getShippingAreas });
